import React, { useState } from "react";
import { apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";

export interface NewPatientFormProps {
  onPatientAdded?: (patient?: unknown) => void;
}

interface FormDataState {
  mobile: string;
  name: string;
  age: string;
  gender: string;
  area: string;
  reference_source: string;
  medical_alerts: string;
  notes: string;
}

const NewPatientForm = ({ onPatientAdded }: NewPatientFormProps) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<FormDataState>({
    mobile: "",
    name: "",
    age: "",
    gender: "",
    area: "",
    reference_source: "Board/Walk-in",
    medical_alerts: "",
    notes: "",
  });
  const [autoQueue, setAutoQueue] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const normalizeDate = (localDate: Date | string = new Date()): string => {
    const d = new Date(localDate);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split("T")[0];
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      const numeric = value.replace(/\D/g, "");
      // Allow >10 digits so UI can show "too long" feedback (still validates on submit).
      if (numeric.length > 15) return;
      setFormData((prev) => ({ ...prev, mobile: numeric }));
      if (errors.mobile) {
        setErrors((prev) => ({ ...prev, mobile: "" }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const mobileLen = formData.mobile.length;
  const mobileState =
    mobileLen === 0 ? "idle" : mobileLen < 10 ? "warn" : mobileLen === 10 ? "ok" : "error";

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.mobile || formData.mobile.length !== 10) {
      newErrors.mobile = "Valid 10-digit mobile required";
    }
    if (!formData.name.trim()) {
      newErrors.name = "Patient name is required";
    }
    if (!formData.gender) {
      newErrors.gender = "Select gender";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) {
      showToast({ message: "Please fix form errors before submitting.", type: "warning" });
      return;
    }

    setLoading(true);

    try {
      const patientId = formData.mobile.trim();

      const payload = {
        name: formData.name.trim(),
        phone: patientId,
        mobile: patientId,
        patient_id: patientId,
        age:
          formData.age && !isNaN(parseInt(formData.age, 10))
            ? parseInt(formData.age, 10)
            : null,
        gender: formData.gender || null,
        reference_source: formData.reference_source || "Board/Walk-in",
        area: formData.area || null,
        location: formData.area || null,
      };

      let patientRes: { patient?: unknown };

      try {
        patientRes = (await apiPost("/api/patients/", payload)) as { patient?: unknown };
      } catch (err: unknown) {
        const e = err as { status?: number; response?: { status?: number } };
        if (e?.status === 409 || e?.response?.status === 409) {
          patientRes = { patient: payload };
        } else {
          throw err;
        }
      }

      if (autoQueue) {
        try {
          await apiPost("/api/visits/", {
            patient_id: patientId,
            visit_date: normalizeDate(),
            visit_type: "New",
            remarks: "New Patient Checkup",
            created_source: "reception_queue",
          });
        } catch (err: unknown) {
          const e = err as { status?: number; response?: { status?: number }; message?: string };
          if (
            e?.status === 409 ||
            e?.response?.status === 409 ||
            (e?.message &&
              (e.message.includes("race condition") || e.message.includes("already exists")))
          ) {
            showToast({ message: "⚠️ Patient already queued for today.", type: "warning" });
          } else {
            throw err;
          }
        }
      }

      showToast({
        message: autoQueue
          ? `✅ ${formData.name} Registered & Queued!`
          : `✅ ${formData.name} Registered Successfully!`,
        type: "success",
      });

      onPatientAdded?.(patientRes.patient ?? payload);

      setFormData({
        mobile: "",
        name: "",
        age: "",
        gender: "",
        area: "",
        reference_source: "Board/Walk-in",
        medical_alerts: "",
        notes: "",
      });
    } catch (err) {
      console.error(err);
      const e = err as { body?: { error?: string }; response?: { data?: { error?: string } }; message?: string };
      const backendMsg = e?.body?.error || e?.response?.data?.error || e?.message || "Registration failed";

      showToast({
        message: backendMsg.includes("exists")
          ? "⚠️ Patient with this mobile already exists."
          : "❌ " + backendMsg,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ui-card rc-form-card">
      <form onSubmit={handleSubmit} className="rc-form">
        <div className="rc-form-grid rc-grid-200">
          <div className="rc-field">
            <label className="rc-label">Mobile Number *</label>
            <input
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
              autoComplete="off"
              inputMode="numeric"
              className={`rc-input rc-input-mobile rc-input-state-${errors.mobile ? "error" : mobileState}`}
              placeholder="10-digit number"
            />
            {errors.mobile && <div className="rc-error-text">{errors.mobile}</div>}
          </div>
          <div className="rc-field">
            <label className="rc-label">Full Name *</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className={`rc-input ${errors.name ? "rc-input-error" : ""}`}
              placeholder="Full Name"
              autoCapitalize="words"
            />
          </div>
        </div>

        <div className="rc-form-grid rc-grid-150">
          <div className="rc-field">
            <label className="rc-label">Age</label>
            <input name="age" type="number" value={formData.age} onChange={handleChange} className="rc-input" placeholder="Yrs" />
          </div>
          <div className="rc-field">
            <label className="rc-label">Gender *</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className={`rc-input ${errors.gender ? "rc-input-error" : ""}`}
            >
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="rc-field">
            <label className="rc-label">Area / City</label>
            <input name="area" value={formData.area} onChange={handleChange} className="rc-input" placeholder="e.g. Trichy Cantt" />
          </div>
        </div>

        <div className="rc-form-grid rc-grid-1">
          <div className="rc-field">
            <label className="rc-label">
              Reference <span className="rc-label-note">(Optional)</span>
            </label>
            <select name="reference_source" value={formData.reference_source} onChange={handleChange} className="rc-input">
              {["Board/Walk-in", "Old Patient", "Referred by Old Patient", "Doctor's Referral", "Online/Media"].map((ref) => (
                <option key={ref} value={ref}>{ref}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="rc-field">
          <label className="rc-label rc-label-alert">
            ⚠️ Medical Alerts / Allergies
          </label>
          <textarea
            name="medical_alerts"
            value={formData.medical_alerts}
            onChange={handleChange}
            rows={2}
            className={`rc-input rc-input-alert rc-textarea ${formData.medical_alerts ? "rc-input-alert-strong" : "rc-input-alert-soft"}`}
            placeholder="e.g. Diabetic, BP, Drug Allergies..."
          />
        </div>

        <div className="rc-form-footer">
          <label className="rc-checkbox-label">
            <input type="checkbox" checked={autoQueue} onChange={(e) => setAutoQueue(e.target.checked)} className="rc-checkbox" />
            Auto-Queue for Checkup
          </label>

          <Button type="submit" variant="primary" isLoading={loading}>
            {autoQueue ? "Register & Queue Visit" : "Register Only"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewPatientForm;
